import { type FC } from 'react';
import { useLazyLoadQuery, graphql } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import RelatedPhotosGrid from './RelatedPhotosGrid.tsx';
import RelatedPhotosHeader from './RelatedPhotosHeader.tsx';
import RelatedPhotosPill from './RelatedPhotosPill.tsx';
import RelatedPhotosViewAllButton from './RelatedPhotosViewAllButton.tsx';

import styles from './RelatedPhotosRenderer.scss';

import { type RelatedPhotosRendererQuery } from './__generated__/RelatedPhotosRendererQuery.graphql.ts';
import { type PhotoTagType } from './types.ts';

const DISPLAY_NUM_STYLE_PILLS = 5;

export type RelatedPhotosRendererProps = {
    photoId: string;
    isMobile?: boolean;
    styleTag?: PhotoTagType | null;
    roomTag?: PhotoTagType | null;
    userId: string;
};

const RelatedPhotosRenderer: FC<RelatedPhotosRendererProps> = props => {
    const { isMobile = false, photoId, styleTag, roomTag, userId } = props;

    const styleTagUrlLabel = styleTag?.urlLabel;
    const roomTagUrlLabel = roomTag?.urlLabel;

    const data = useLazyLoadQuery<RelatedPhotosRendererQuery>(
        graphql`
            query RelatedPhotosRendererQuery(
                $style: [String!]
                $room: [String!]
                $userId: String
                $hasUserId: Boolean!
            ) {
                viewer {
                    photosSearchBrowseQuery(page: 0, first: 60, style: $style, room: $room) {
                        facets {
                            imageStyleFacet {
                                label
                                name
                            }
                            imageRoomTypeFacet {
                                label
                                name
                            }
                        }
                        numberOfPages
                        ...RelatedPhotosGrid_photos
                    }
                }
            }
        `,
        {
            style: [styleTagUrlLabel || ''],
            room: [roomTagUrlLabel || ''],
            userId,
            hasUserId: !!userId,
        },
        { fetchPolicy: 'store-or-network' }
    );

    const photosSearchBrowseQuery = data.viewer.photosSearchBrowseQuery;
    const facets = photosSearchBrowseQuery?.facets;
    const styleFacet = facets?.imageStyleFacet || [];
    const roomTypeFacet = facets?.imageRoomTypeFacet || [];
    const numberOfPages = photosSearchBrowseQuery?.numberOfPages || 0;

    const showRoomTypeFacet = !styleTagUrlLabel;
    const displayFacet = showRoomTypeFacet ? roomTypeFacet : styleFacet;

    return (
        <div className={styles.wrapper}>
            <RelatedPhotosHeader roomTag={roomTag} />
            <div className={styles.pillWrapper}>
                {displayFacet
                    .filter(filterNulls)
                    .slice(0, DISPLAY_NUM_STYLE_PILLS)
                    .map((facet, i) => {
                        return (
                            <RelatedPhotosPill
                                key={`${facet.label}-${i}`}
                                roomTag={roomTag}
                                facet={facet}
                                isRoomTypeFacet={showRoomTypeFacet}
                            />
                        );
                    })}
            </div>
            {numberOfPages > 0 && (
                <>
                    <RelatedPhotosGrid
                        isMobile={isMobile}
                        photoId={photoId}
                        photos={photosSearchBrowseQuery}
                    />
                    <div className={styles.viewAllWrapper}>
                        <RelatedPhotosViewAllButton
                            isMobile={isMobile}
                            styleTag={styleTag}
                            roomTag={roomTag}
                            numberOfPages={numberOfPages}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default RelatedPhotosRenderer;
